// Последовательная запись в нижней шторке: услуга → время → контакты →
// подтверждение → готово. Каждый шаг — запись истории (кнопка «Назад» работает).
import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { CaretLeft, CheckCircle } from '@phosphor-icons/react';
import { Button } from '@astryxdesign/core/Button';
import { IconButton } from '@astryxdesign/core/IconButton';
import { List, ListItem } from '@astryxdesign/core/List';
import { Text } from '@astryxdesign/core/Text';
import { TextInput } from '@astryxdesign/core/TextInput';
import { TextArea } from '@astryxdesign/core/TextArea';
import { VStack, HStack } from '@astryxdesign/core/Stack';
import { FormLayout } from '@astryxdesign/core/FormLayout';
import { Banner } from '@astryxdesign/core/Banner';
import { MetadataList, MetadataListItem } from '@astryxdesign/core/MetadataList';
import { useToast } from '@astryxdesign/core/Toast';
import { normalizePhone } from '@shared/business-schema';
import { Sheet } from '@/components/Sheet';
import { PhoneInput } from '@/components/PhoneInput';
import { publicApi } from '@/lib/api/public';
import type { BookingView, PublicTenant } from '@/lib/api/schemas';
import { ApiError, errorText } from '@/lib/errors';
import { formatPhone } from '@/lib/phone';
import { money } from '@/lib/money';
import { loadContact, saveBooking, saveContact, type ContactDraft } from '@/lib/store';
import { dateTimeLabel, durationLabel } from '@/lib/time';
import { ReminderControls } from './ReminderControls';
import { SlotPicker } from './SlotPicker';
import { useBookingRoute, useLoadedTenant, useSlug, type BookStep } from './hooks';

const TITLES: Record<BookStep, string> = {
  service: 'Выберите услугу',
  time: 'Когда приехать',
  contact: 'Ваши контакты',
  confirm: 'Проверьте запись',
  done: 'Вы записаны',
};

export function BookingSheet() {
  const t = useLoadedTenant();
  const slug = useSlug();
  const route = useBookingRoute();
  const { step, serviceId, startsAt } = route;
  const service = t.services.find((s) => s.id === serviceId) ?? null;
  const [contact, setContact] = useState<ContactDraft>(() => loadContact() ?? { name: '', phone: '', car: '' });
  const [comment, setComment] = useState('');
  const [result, setResult] = useState<{ booking: BookingView; token: string } | null>(null);
  // ошибки всех полей показываются после попытки перейти дальше
  const [contactAttempted, setContactAttempted] = useState(false);

  // Ключ идемпотентности живёт, пока не меняются данные записи: повторная
  // отправка после обрыва связи вернёт ту же запись и ту же ссылку.
  const payloadSig = `${serviceId}|${startsAt}|${contact.name}|${contact.phone}|${contact.car}|${comment}`;
  const idempotencyKey = useMemo(() => crypto.randomUUID(), [payloadSig]); // eslint-disable-line react-hooks/exhaustive-deps

  // Шаг без нужных данных (например, после перезагрузки) возвращает на предыдущий
  useEffect(() => {
    if (!step) return;
    if ((step === 'time' || step === 'contact' || step === 'confirm') && !service) route.go('service', {}, { replace: true });
    else if ((step === 'contact' || step === 'confirm') && !startsAt) route.go('time', {}, { replace: true });
    else if (step === 'done' && !result) route.close();
  }, [step, service, startsAt, result, route]);

  const open = route.open;
  const current: BookStep = step ?? 'service';
  const canGoBack = current !== 'service' && current !== 'done';

  let body: React.ReactNode = null;
  let footer: React.ReactNode = null;

  if (current === 'service') {
    body = (
      <List hasDividers density="spacious">
        {t.services.map((s) => (
          <ListItem
            key={s.id}
            label={s.name}
            description={`${durationLabel(s.duration_minutes)}${s.description ? ` · ${s.description}` : ''}`}
            isDisabled={s.resource_count === 0}
            isSelected={s.id === serviceId}
            endContent={
              <Text weight="semibold" hasTabularNumbers textWrap="nowrap">
                {money(s.price_cents, t.currency, s.price_is_from)}
              </Text>
            }
            onClick={() => route.go('time', { svc: s.id, at: s.id === serviceId ? startsAt : null })}
          />
        ))}
      </List>
    );
  }

  if (current === 'time' && service) {
    body = (
      <VStack gap={3}>
        <Text color="secondary">
          {service.name} · {durationLabel(service.duration_minutes)}. Время указано по часовому поясу студии.
        </Text>
        <SlotPicker
          slug={slug}
          tz={t.timezone}
          serviceId={service.id}
          value={startsAt}
          onChange={(at) => route.go('time', { at }, { replace: true })}
        />
      </VStack>
    );
    footer = (
      <Button
        label={startsAt ? `Дальше: контакты` : 'Выберите время'}
        variant="primary"
        size="lg"
        width="100%"
        isDisabled={!startsAt}
        onClick={() => route.go('contact')}
      />
    );
  }

  if (current === 'contact') {
    body = (
      <ContactForm
        value={contact}
        onChange={setContact}
        comment={comment}
        onComment={setComment}
        showAllErrors={contactAttempted}
        onSubmit={() => route.go('confirm')}
      />
    );
    footer = (
      <Button
        label="Дальше: проверить"
        variant="primary"
        size="lg"
        width="100%"
        onClick={() => {
          const errs = contactErrors(contact);
          if (errs) {
            setContactAttempted(true);
            const first = (['name', 'phone', 'car'] as const).find((k) => errs[k]);
            document.querySelector<HTMLInputElement>(`[data-contact-form] [data-field="${first}"] input`)?.focus();
            return;
          }
          route.go('confirm');
        }}
      />
    );
  }

  if (current === 'confirm' && service && startsAt) {
    body = <ConfirmSummary tenant={t} serviceId={service.id} startsAt={startsAt} contact={contact} comment={comment} />;
    footer = (
      <SubmitBooking
        tenant={t}
        input={{ slug, serviceId: service.id, startsAt, comment, idempotencyKey, ...contact }}
        onDone={(r) => {
          setResult(r);
          saveContact(contact);
          saveBooking(slug, { token: r.token, startsAt: r.booking.starts_at, serviceName: r.booking.service_name, savedAt: new Date().toISOString() });
          setComment('');
          route.go('done', { at: null }, { replace: true });
        }}
        onSlotTaken={() => route.go('time', { at: null })}
      />
    );
  }

  if (current === 'done' && result) {
    body = <DoneView result={result} />;
  }

  return (
    <Sheet
      open={open}
      onOpenChange={(o) => !o && route.close()}
      title={TITLES[current]}
      description={current === 'service' ? t.name : undefined}
      focusKey={current}
      headerStart={
        canGoBack ? <IconButton label="Назад" icon={<CaretLeft size={20} />} variant="ghost" onClick={route.back} /> : null
      }
      footer={footer}
    >
      {body}
    </Sheet>
  );
}

function contactErrors(c: ContactDraft) {
  const e: Partial<Record<keyof ContactDraft, string>> = {};
  if (!c.name.trim()) e.name = 'Укажите имя';
  if (!normalizePhone(c.phone)) e.phone = 'Номер в формате +7 900 000-00-00';
  if (!c.car.trim()) e.car = 'Укажите марку и модель';
  return Object.keys(e).length ? e : null;
}

function ContactForm({
  value,
  onChange,
  comment,
  onComment,
  onSubmit,
  showAllErrors,
}: {
  value: ContactDraft;
  onChange: (c: ContactDraft) => void;
  comment: string;
  onComment: (s: string) => void;
  onSubmit: () => void;
  showAllErrors: boolean;
}) {
  const [touched, setTouched] = useState<Partial<Record<keyof ContactDraft, boolean>>>({});
  const errors = contactErrors(value) ?? {};
  const status = (k: keyof ContactDraft) =>
    (touched[k] || showAllErrors) && errors[k] ? { type: 'error' as const, message: errors[k] as string } : undefined;
  const set = (k: keyof ContactDraft) => (v: string) => onChange({ ...value, [k]: v });
  // Пустое поле, которое только тронули, не ругаем: ошибка — после ввода или после «Дальше»
  const blur = (k: keyof ContactDraft) => () => {
    if (value[k].trim()) setTouched((t) => ({ ...t, [k]: true }));
  };

  return (
    <form
      data-contact-form
      onSubmit={(e) => {
        e.preventDefault();
        setTouched({ name: true, phone: true, car: true });
        if (!contactErrors(value)) onSubmit();
      }}
    >
      <FormLayout>
        <div data-field="name">
        <TextInput label="Имя" value={value.name} onChange={set('name')} onBlur={blur('name')} status={status('name')} autoComplete="given-name" isRequired />
        </div>
        <div data-field="phone">
        <PhoneInput
          label="Телефон"
          value={value.phone}
          onChange={set('phone')}
          onBlur={blur('phone')}
          status={status('phone')}
          autoComplete="tel"
          placeholder="+7 900 000-00-00"
          description="Студия позвонит, если что-то изменится"
          isRequired
        />
        </div>
        <div data-field="car">
        <TextInput label="Автомобиль" value={value.car} onChange={set('car')} onBlur={blur('car')} status={status('car')} placeholder="Например, Kia Rio" isRequired />
        </div>
        <TextArea label="Комментарий" value={comment} onChange={onComment} rows={2} maxLength={500} isOptional />
      </FormLayout>
      <button type="submit" hidden aria-hidden tabIndex={-1} />
    </form>
  );
}

function ConfirmSummary({
  tenant,
  serviceId,
  startsAt,
  contact,
  comment,
}: {
  tenant: PublicTenant;
  serviceId: string;
  startsAt: string;
  contact: ContactDraft;
  comment: string;
}) {
  const s = tenant.services.find((x) => x.id === serviceId)!;
  const hours = Math.round(tenant.cancel_cutoff_minutes / 60);
  return (
    <VStack gap={4}>
      <MetadataList columns="single" label={{ position: 'start', width: 110 }}>
        <MetadataListItem label="Услуга">{s.name}</MetadataListItem>
        <MetadataListItem label="Приезд">{dateTimeLabel(startsAt, tenant.timezone)}</MetadataListItem>
        <MetadataListItem label="Работа">{durationLabel(s.duration_minutes)}</MetadataListItem>
        <MetadataListItem label="Стоимость">{money(s.price_cents, tenant.currency, s.price_is_from)}</MetadataListItem>
        <MetadataListItem label="Адрес">{tenant.profile.address}</MetadataListItem>
        <MetadataListItem label="Контакты">{`${contact.name}, ${formatPhone(normalizePhone(contact.phone))}`}</MetadataListItem>
        <MetadataListItem label="Автомобиль">{contact.car}</MetadataListItem>
        {comment ? <MetadataListItem label="Комментарий">{comment}</MetadataListItem> : null}
      </MetadataList>
      <Text type="supporting" color="secondary">
        Перенести или отменить запись можно по ссылке не позднее чем за {hours} ч до приезда. Оплата — в студии.
      </Text>
    </VStack>
  );
}

function SubmitBooking({
  tenant,
  input,
  onDone,
  onSlotTaken,
}: {
  tenant: PublicTenant;
  input: Parameters<typeof publicApi.createBooking>[0];
  onDone: (r: { booking: BookingView; token: string }) => void;
  onSlotTaken: () => void;
}) {
  const toast = useToast();
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setError(null);
    try {
      const r = await publicApi.createBooking(input);
      void qc.invalidateQueries({ queryKey: ['slots', input.slug] });
      onDone(r);
    } catch (e) {
      const code = e instanceof ApiError ? e.code : 'unknown';
      if (code === 'slot_taken' || code === 'too_soon' || code === 'not_reception_time') {
        void qc.invalidateQueries({ queryKey: ['slots', input.slug] });
        toast({ body: errorText(e), type: 'error' });
        onSlotTaken();
        return;
      }
      setError(errorText(e));
    }
  };

  return (
    <VStack gap={2}>
      {error ? <Banner status="error" title="Запись не отправлена" description={error} /> : null}
      <Button
        label={`Записаться на ${dateTimeLabel(input.startsAt, tenant.timezone).split(', ').pop()}`}
        variant="primary"
        size="lg"
        width="100%"
        clickAction={submit}
      />
    </VStack>
  );
}

function DoneView({ result }: { result: { booking: BookingView; token: string } }) {
  const navigate = useNavigate();
  const toast = useToast();
  const b = result.booking;
  const link = `${location.origin}/s/${b.tenant.slug}/my#t=${result.token}`;
  return (
    <VStack gap={4}>
      <HStack gap={3} align="center">
        <CheckCircle size={40} weight="fill" className="shrink-0 text-accent" aria-hidden />
        <VStack gap={0.5}>
          <Text weight="semibold" type="large">
            {dateTimeLabel(b.starts_at, b.tenant.timezone)}
          </Text>
          <Text color="secondary">
            {b.service_name} · {b.resource_name}
          </Text>
        </VStack>
      </HStack>
      {b.is_demo ? (
        <Banner status="info" title="Это тестовая запись" description="Студия ещё в демо-режиме: запись не попадёт в работу." />
      ) : null}
      <Text color="secondary">
        Ссылка на запись сохранена на этом устройстве в разделе «Моя запись». Сохраните её, чтобы открыть с другого телефона.
      </Text>
      <ReminderControls booking={b} token={result.token} />
      <HStack gap={2}>
        <Button
          label="Скопировать ссылку"
          width="100%"
          clickAction={async () => {
            try {
              await navigator.clipboard.writeText(link);
              toast({ body: 'Ссылка скопирована' });
            } catch {
              toast({ body: 'Не получилось скопировать. Ссылка есть в разделе «Моя запись».', type: 'error' });
            }
          }}
        />
        <Button label="Моя запись" variant="primary" width="100%" onClick={() => navigate(`/s/${b.tenant.slug}/my`, { replace: true })} />
      </HStack>
    </VStack>
  );
}
